import React, { useState, useEffect } from "react";
import { BiDownload, BiFilter, BiRefresh } from "react-icons/bi";
import { useFinancialReport } from "../../../hooks";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
  translateStatus,
} from "../../../utils/formatters";
import {
  ReportFilters,
  ReportTabs,
  KPICard,
  DataTable,
  ExportModal,
} from "./shared";

const FinancialReport = () => {
  const [activeTab, setActiveTab] = useState("summary");
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [filters, setFilters] = useState({
    period: "month",
    startDate: null,
    endDate: null,
  });

  const { loading, error, getSummary, getTransactions } = useFinancialReport();

  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState({
    data: [],
    pagination: {},
  });

  useEffect(() => {
    loadData();
  }, [activeTab, filters]);

  const loadData = async () => {
    try {
      if (activeTab === "summary") {
        const data = await getSummary(filters);
        setSummary(data);
      } else {
        const result = await getTransactions({ ...filters, page: 1 });
        setTransactions(result);
      }
    } catch (error) {
      console.error("Erreur chargement:", error);
    }
  };

  const handlePageChange = async (page) => {
    const result = await getTransactions({ ...filters, page });
    setTransactions(result);
  };

  const handleExport = async (format, options) => {
    // Implémenter l'export
    console.log("Export:", format, options);
  };

  const tabs = [
    { id: "summary", label: "Récapitulatif" },
    {
      id: "transactions",
      label: "Transactions",
      count: transactions.pagination?.total,
    },
  ];

  const transactionColumns = [
    {
      key: "code",
      label: "Code",
      render: (row) => <span className="font-mono">{row.code}</span>,
    },
    {
      key: "clientName",
      label: "Client",
      render: (row) => row.clientName || "Anonyme",
    },
    {
      key: "amount",
      label: "Montant",
      render: (row) => (
        <span className="font-semibold">{formatCurrency(row.amount)}</span>
      ),
    },
    {
      key: "method",
      label: "Méthode",
      render: (row) => {
        const methods = {
          CASH: "Espèces",
          CARD: "Carte bancaire",
          MOBILE_MONEY: "Mobile Money",
          FEDAPAY: "Fedapay",
        };
        return methods[row.method] || row.method;
      },
    },
    {
      key: "status",
      label: "Statut",
      render: (row) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            row.status === "COMPLETED"
              ? "bg-green-100 text-green-800"
              : row.status === "PENDING"
                ? "bg-yellow-100 text-yellow-800"
                : row.status === "FAILED"
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
          }`}
        >
          {translateStatus(row.status, "payment")}
        </span>
      ),
    },
    {
      key: "type",
      label: "Type",
      render: (row) => {
        const types = {
          appointment: "Rendez-vous",
          subscription: "Abonnement",
          product: "Produit",
          gift_card: "Carte cadeau",
        };
        return types[row.type] || row.type;
      },
    },
    {
      key: "createdAt",
      label: "Date",
      render: (row) =>
        new Date(row.createdAt).toLocaleDateString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ];

  if (error) {
    return (
      <div className="p-6 text-center text-red-600">
        Une erreur est survenue : {error}
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* En-tête */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-poppins font-semibold text-[20px] text-title">
          Rapport financier
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiFilter size={18} />
            Filtres
          </button>
          <button
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 transition-colors"
          >
            <BiDownload size={18} />
            Exporter
          </button>
          <button
            onClick={loadData}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <BiRefresh size={18} />
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <ReportFilters
          filters={filters}
          onChange={setFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Onglets */}
      <ReportTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {/* Contenu */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-500"></div>
        </div>
      ) : (
        <>
          {activeTab === "summary" && summary && (
            <div className="space-y-6">
              {/* KPIs Chiffre d'affaires */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard
                  title="CA Total"
                  value={formatCurrency(summary.turnover.total)}
                  icon="💰"
                  color="blue"
                />
                <KPICard
                  title="CA en ligne"
                  value={formatCurrency(summary.turnover.online)}
                  icon="🌐"
                  color="green"
                />
                <KPICard
                  title="CA en boutique"
                  value={formatCurrency(summary.turnover.inStore)}
                  icon="🏪"
                  color="purple"
                />
                <KPICard
                  title="Paiements en attente"
                  value={formatCurrency(summary.pendingPayments.amount)}
                  icon="⏳"
                  color="yellow"
                />
              </div>

              {/* Détail par méthode de paiement */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-poppins font-semibold text-[16px] mb-4">
                  Répartition par mode de paiement
                </h3>
                <div className="space-y-3">
                  {Object.entries(summary.turnover.byMethod).map(
                    ([method, amount]) => {
                      const percentage =
                        (amount / summary.turnover.total) * 100;
                      const methods = {
                        CASH: "Espèces",
                        CARD: "Carte bancaire",
                        MOBILE_MONEY: "Mobile Money",
                        FEDAPAY: "Fedapay",
                      };

                      return (
                        <div key={method} className="flex items-center gap-4">
                          <span className="w-32 font-nunito text-[14px]">
                            {methods[method] || method}
                          </span>
                          <div className="flex-1">
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-secondary-500 rounded-full"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                          </div>
                          <span className="w-24 font-nunito text-[14px] text-right">
                            {formatCurrency(amount)}
                          </span>
                          <span className="w-16 font-nunito text-[14px] text-gray-500">
                            {formatPercentage(percentage)}
                          </span>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>

              {/* Passifs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <KPICard
                  title="Remboursements"
                  value={formatCurrency(summary.refunds.amount)}
                  subtitle={`${formatNumber(summary.refunds.count)} transactions`}
                  icon="↩️"
                  color="red"
                />
                <KPICard
                  title="Passifs abonnements"
                  value={formatCurrency(summary.liabilities.subscriptions)}
                  icon="🔄"
                  color="orange"
                />
                <KPICard
                  title="Passifs cartes cadeaux"
                  value={formatCurrency(summary.liabilities.giftCards)}
                  icon="🎁"
                  color="pink"
                />
              </div>

              {/* Période */}
              <div className="text-sm text-gray-500 text-right">
                Période du{" "}
                {new Date(summary.period.start).toLocaleDateString("fr-FR")} au{" "}
                {new Date(summary.period.end).toLocaleDateString("fr-FR")}
              </div>
            </div>
          )}

          {activeTab === "transactions" && (
            <DataTable
              columns={transactionColumns}
              data={transactions.data}
              pagination={transactions.pagination}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Modal d'export */}
      {showExport && (
        <ExportModal
          onClose={() => setShowExport(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default FinancialReport;
